import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  People as PeopleIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  AdminPanelSettings as AdminIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { userManagementService, UserCounts } from '../services/userManagementService';

interface UserCountWidgetProps {
  refreshTrigger?: number; // Optional prop to trigger refresh
}

/**
 * UserCountWidget - Interactive user statistics widget for the admin dashboard
 * 
 * Features:
 * - Displays real-time user counts by role and status
 * - Clickable main card navigates to User Management page
 * - Individual stat items navigate with pre-applied filters:
 *   - Role stats (Patients, Caregivers, Admins) filter by role
 *   - Status stats (Active, Disabled) filter by status
 * - Hover effects and visual feedback for better UX
 * - Tooltips provide clear navigation hints
 */

export function UserCountWidget({ refreshTrigger }: UserCountWidgetProps) {
  const [userCounts, setUserCounts] = useState<UserCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadUserCounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userManagementService.getUserCounts();
      setUserCounts(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user counts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserCounts();
  }, [refreshTrigger]);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!userCounts) {
    return null;
  }

  const statItems = [
    {
      title: 'Total Users',
      value: userCounts.total,
      icon: <PeopleIcon />,
      color: '#3478C8',
      bgColor: '#EAF3FA',
      onClick: () => handleCardClick(),
    },
    {
      title: 'Patients',
      value: userCounts.byRole.patients,
      icon: <PersonIcon />,
      color: '#2196f3',
      bgColor: '#e3f2fd',
      onClick: () => handleStatClick('PATIENT'),
    },
    {
      title: 'Caregivers',
      value: userCounts.byRole.caregivers,
      icon: <HospitalIcon />,
      color: '#ff9800',
      bgColor: '#fff3e0',
      onClick: () => handleStatClick('CAREGIVER'),
    },
    {
      title: 'Admins',
      value: userCounts.byRole.admins,
      icon: <AdminIcon />,
      color: '#f44336',
      bgColor: '#ffebee',
      onClick: () => handleStatClick('ADMIN'),
    },
    {
      title: 'Active',
      value: userCounts.active,
      icon: <CheckCircleIcon />,
      color: '#4caf50',
      bgColor: '#e8f5e8',
      onClick: () => handleStatClick(undefined, 'active'),
    },
    {
      title: 'Disabled',
      value: userCounts.disabled,
      icon: <BlockIcon />,
      color: '#9e9e9e',
      bgColor: '#f5f5f5',
      onClick: () => handleStatClick(undefined, 'disabled'),
    },
  ];

  const handleCardClick = () => {
    navigate('/admin/users');
  };

  const handleStatClick = (role?: 'PATIENT' | 'CAREGIVER' | 'ADMIN', status?: 'active' | 'disabled') => {
    const params = new URLSearchParams();
    if (role) {
      params.set('role', role);
    }
    if (status) {
      params.set('disabled', status === 'disabled' ? 'true' : 'false');
    }
    navigate(`/admin/users?${params.toString()}`);
  };

  return (
    <Tooltip title="Click to view User Management" arrow>
      <Card 
        sx={{ 
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 3,
          }
        }}
        onClick={handleCardClick}
      >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" component="h2">
            User Statistics
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label="Live"
              color="success"
              size="small"
              variant="outlined"
            />
            <Tooltip title="View User Management">
              <IconButton size="small" color="primary">
                <ArrowForwardIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Grid container spacing={2}>
          {statItems.map((item, index) => (
            <Grid item xs={6} sm={4} md={2} key={index}>
              <Box
                onClick={(e) => {
                  e.stopPropagation(); // Prevent card click when clicking on stat
                  item.onClick();
                }}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: item.bgColor,
                  textAlign: 'center',
                  border: `1px solid ${item.color}20`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: 2,
                    backgroundColor: item.bgColor,
                    opacity: 0.9,
                  },
                }}
              >
                <Box
                  sx={{
                    color: item.color,
                    mb: 1,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </Box>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{
                    color: item.color,
                    fontWeight: 'bold',
                    mb: 0.5,
                  }}
                >
                  {item.value}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                  }}
                >
                  {item.title}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Summary */}
        <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
          <Typography variant="body2" color="text.secondary">
            <strong>Summary:</strong> {userCounts.total} total users with {userCounts.active} active 
            ({Math.round((userCounts.active / userCounts.total) * 100)}%) and {userCounts.disabled} disabled.
          </Typography>
          <Typography variant="body2" color="primary.main" sx={{ mt: 1, fontStyle: 'italic' }}>
            Click to manage users →
          </Typography>
        </Box>
      </CardContent>
    </Card>
    </Tooltip>
  );
}
